import React, { useState } from 'react';
import gql from 'graphql-tag';
import styled from '@emotion/styled';
import { useQuery, useMutation } from '@apollo/client';
import { withRouter } from 'react-router';
import { ImpulseSpinner } from 'react-spinners-kit';

import * as actions from '~/actions';

import { useDispatch, useI18n, useLogger, useSelector } from '~Hooks';
import NumberInput from '~ComponentsImproved/Numpad';
import { ClaimDevice as ClaimDeviceQuery, ClaimDeviceVariables, getDevices } from './__queries__';

export const GET_DEVICES = gql`
  query getDevices($venueId: ID!, $companyId: ID!) {
    devices: allPOSDevices(filter: { company: { id: $companyId }, venue: { id: $venueId } }) {
      id
      name
      claimed
    }

    venue: Venue(id: $venueId) {
      id
      organizationId
    }
  }
`;

export const CLIAM_DEVICE = gql`
  mutation ClaimDevice($deviceId: ID!, $lastSeen: DateTime!) {
    updatePOSDevice(where: { id: $deviceId }, data: { claimed: true, lastSeen: $lastSeen }) {
      id
      name
    }
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex: 1;
  width: 100%;
  padding-top: 4px;
  align-items: center;
  justify-content: space-between;
  flex-direction: column;
  overflow: hidden;
`;

const DeviceContainer = styled.div`
  border: 1px solid rgba(0, 0, 0, 0.7);
  border-radius: 4px;
  margin: 0 48px 4px 48px;
  display: flex;
  flex-direction: column;
  overflow: scroll;
  width: 80%;
  flex: 1;
`;

const Device = styled.div<{ selected: boolean; claimed?: boolean }>`
  padding: 18px 16px;
  font-size: 20px;
  text-overflow: ellipsis;
  background-color: ${(props) => (props.selected ? 'rgba(0, 0, 0, 0.1)' : '#fff')};

  border-bottom: 0.5px solid rgba(0, 0, 0, 0.4);
  border-top: 0.5px solid rgba(0, 0, 0, 0.4);

  :active {
    background-color: rgba(0, 0, 0, 0.2);
  }

  opacity: ${(props) => (props.claimed ? 0.7 : 1)};
`;

const ButtonContainer = styled.div`
  margin: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 90px;
`;

const UpdateButton = styled.div<{ valid: boolean }>`
  background: gray;
  text-align: center;
  box-shadow: inset 0px 1px 3px rgba(0, 0, 0, 0.2);
  color: #fff;
  border-radius: 2px;
  padding: 20px 40px;
  margin-bottom: 8px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  opacity: ${({ valid }) => (valid ? 1 : 0.6)};
`;

const ClaimDevice = () => {
  const logger = useLogger('claim-device');
  const { i18n } = useI18n();
  const [, , venueId] = location.hash.split('/');
  const companyId = useSelector((state) => state.company);
  const dispatch = useDispatch();
  const [showPasswordInput, setShowPasswordInput] = useState(true);
  const [passwordFailed, setPasswordFailed] = useState(false);

  const [selectedDevice, setSelectedDevice] = useState<string | null>(null);
  const [loadingUpdate, setLoadingUpdate] = useState(false);

  const { data, loading } = useQuery<getDevices>(GET_DEVICES, {
    variables: { venueId, companyId },
    skip: !venueId || !companyId,
    fetchPolicy: 'cache-and-network',
  });

  const [claimDevice] = useMutation<ClaimDeviceQuery, ClaimDeviceVariables>(CLIAM_DEVICE, {
    refetchQueries: ['getDevices'],
    awaitRefetchQueries: true,
  });

  if (loading) {
    return (
      <Wrapper>
        <ImpulseSpinner size={64} />
      </Wrapper>
    );
  }

  const devices = data?.devices ?? [];

  const handleUpdate = async () => {
    if (!selectedDevice || !data?.venue) {
      return;
    }

    setLoadingUpdate(true);

    const corporateId = data.venue.organizationId.replace(/\D/g, '');

    let id = selectedDevice;
    const device = data.devices.find((device) => device.id === id)!;
    let name = device.name;

    dispatch({
      type: actions.MESSAGE_RECEIPT_COUNTER,
      receiptCounter: 0,
    });

    if (!device.claimed) {
      try {
        const { data: result, errors } = await claimDevice({
          variables: { deviceId: selectedDevice, lastSeen: new Date() },
        });

        if (result?.updatePOSDevice) {
          id = result.updatePOSDevice.id;
          name = result.updatePOSDevice.name;
        } else {
          setLoadingUpdate(false);
          console.error(errors);
        }
      } catch (error) {
        setLoadingUpdate(false);
        console.error(error);
      }
    }
    setLoadingUpdate(false);

    // Update the name
    dispatch({
      type: 'SET_COMPLIANCE_NAME',
      name,
    });
    dispatch({
      type: 'SET_COMPLIANCE_ID',
      id,
      corporateId,
    });

    logger.info('Updating Settings (change device)', {
      fields: { name, id, corporateId },
    });

    window.postMessageBridge({
      updateTaxInformation: {
        deviceName: name,
        deviceCCU: id,
        deviceTaxID: corporateId,
      },
    });
  };

  if (passwordFailed || showPasswordInput) {
    return (
      <Wrapper>
        {passwordFailed ? (
          <h2>{i18n('Settings.NotAllowed')}</h2>
        ) : (
          <NumberInput
            onClose={() => setPasswordFailed(true)}
            onSubmit={(value) => {
              if (value !== '1111') {
                setPasswordFailed(true);
              }
              setShowPasswordInput(false);
            }}
          />
        )}
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <DeviceContainer>
        {devices
          .filter((d) => !d.claimed)
          .map((device) => (
            <Device
              key={device.id}
              selected={device.id === selectedDevice}
              onTouchStart={() => {
                if (!loadingUpdate) {
                  setSelectedDevice(device.id);
                }
              }}>
              {device.name}
            </Device>
          ))}
      </DeviceContainer>

      <DeviceContainer>
        {devices
          .filter((d) => d.claimed)
          .map((device) => (
            <Device
              key={device.id}
              claimed
              selected={device.id === selectedDevice}
              onTouchStart={() => {
                if (!loadingUpdate) {
                  setSelectedDevice(device.id);
                }
              }}>
              {device.name} {device.claimed ? ' - ' + i18n('Settings.Claimed') : ''}
            </Device>
          ))}
      </DeviceContainer>
      <ButtonContainer>
        <UpdateButton
          onTouchStart={handleUpdate}
          valid={Boolean(selectedDevice) && !loadingUpdate}>
          {i18n('Settings.Claim')}
        </UpdateButton>
        <ImpulseSpinner size={48} loading={loadingUpdate} />
      </ButtonContainer>
    </Wrapper>
  );
};

export default withRouter(ClaimDevice);
